import { ReactNode, useEffect, type ComponentProps } from 'react';
import { ErrorBoundary, type FallbackRender } from '@sentry/react';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { AnimatePresence, motion } from 'motion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber, isPhoneNumber } from '@frontend/phone-numbers';
import { Photos } from '@frontend/photos';
import { useIpcShellProvider, shell } from '@frontend/shell-utils';
import { sentry } from '@frontend/tracking';
import { CallPopTypes } from '@frontend/types';
import { PetIcon } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import {
  ActionButton,
  PhoneIcon,
  TextLink,
  Text,
  CaretLeftIconSmall,
  CaretRightIconSmall,
  IconButtonProps,
  XIcon,
  IconButton,
  Chip,
  contactColors,
  Avatar,
  Truncated,
} from '@frontend/design-system';
import { CallPopInterface, useCallPopStateSync } from './store';
import { getHeadofHousehold, isMultipleContactsFromSameHousehold } from './utils';

const BaseNotificationOuter = ({ children }: { children: ReactNode }) => {
  return (
    <div
      style={{
        position: 'relative',
        marginBottom: theme.spacing(0.5),
        borderRadius: theme.borderRadius.medium,
        boxShadow: theme.shadows.heavy,
        overflow: 'hidden',
        width: 380 /* Anymore than that and it adds scroll on Windows */,
      }}
    >
      {children}
    </div>
  );
};

type Props = {
  id: string;
  locationName: string;
  personId: string | undefined;
  photoData?: string;
  isHeadOfHousehold?: boolean;
  isFromSameHousehold?: boolean;
  isMultipleContacts?: boolean;
  callerContext?: string;
  officeNumberName?: string;
  officeNumber?: string;
  callerName?: string;
  callerNumber: string;
  count: number;
  pos: number;
  pets: PetDetails[];
  actions: ReactNode;
  onBack: () => void;
  onForward: () => void;
  onInspect: () => void;
  onAccept?: () => void;
};

type SoftphoneProps = Omit<Props, 'actions'> & {
  options: {
    onReject: (phone: string) => void;
    onAccept: (phone: string) => void;
  };
};

type DefaultProps = Omit<Props, 'actions'> & {
  options: {
    onDismiss: () => void;
  };
};

type WrapperProps = Omit<Props, 'actions'> & {
  notification: CallPopTypes.Notification;
};

const CallPopWrapper = (props: WrapperProps) => {
  const { dismiss, action } = CallPopInterface;

  useEffect(() => {
    action(props.notification.id, 'viewed');
  }, []);

  const notification = props.notification;
  if (notification.payload.type === 'softphone') {
    const onAccept = () => {
      props.onAccept?.();
      action(notification.id, 'answer');
    };
    const onReject = () => action(notification.id, 'hangup');

    return (
      <SoftphoneCallPop
        {...props}
        options={{
          onAccept: onAccept,
          onReject: onReject,
        }}
      />
    );
  } else {
    const onDismiss = () => {
      dismiss(notification.id);
    };
    return (
      <DefaultCallPop
        {...props}
        id={props.notification.id}
        options={{
          onDismiss,
        }}
      />
    );
  }
};

export const CallPop = ({
  locationName,
  personId,
  photoData,
  isHeadOfHousehold = false,
  isFromSameHousehold = true,
  isMultipleContacts = false,
  callerContext,
  officeNumberName,
  officeNumber,
  callerName,
  callerNumber,
  count,
  pos,
  pets,
  actions,
  onBack,
  onForward,
  onInspect,
}: Props) => {
  const formattedNumber = isPhoneNumber(callerNumber) ? formatPhoneNumber(callerNumber, true) : callerNumber;
  const firstPet = isFromSameHousehold && pets?.length > 0 ? pets[0] : '';

  const { t } = useTranslation('pop');
  return (
    <BaseNotificationOuter>
      <section css={{ padding: theme.spacing(2), background: theme.colors.neutral90, color: theme.colors.white }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(0.5),
              borderBottom: `1px solid ${theme.colors.neutral50}`,
              paddingBlockEnd: theme.spacing(1),
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
                {locationName ? (
                  <>
                    <Text as='span' size='medium' weight='light' color='white'>
                      {t('Call to')}
                    </Text>
                    <Text as='span' size='medium' weight='bold' color='white'>
                      {locationName}
                    </Text>
                  </>
                ) : (
                  <Text as='span' size='medium' weight='light' color='white'>
                    {t('Incoming Call')}
                  </Text>
                )}
              </div>
              {count > 1 && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <SpecialIconButton
                    disabled={pos === 1}
                    size='xsmall'
                    label={t('Previous Call')}
                    onClick={onBack}
                    trackingId='phn-pop-previous'
                  >
                    <CaretLeftIconSmall />
                  </SpecialIconButton>
                  {`${pos}/${count}`}
                  <SpecialIconButton
                    disabled={pos === count}
                    size='xsmall'
                    label={t('Next Call')}
                    onClick={onForward}
                    trackingId='phn-pop-next'
                  >
                    <CaretRightIconSmall />
                  </SpecialIconButton>
                </div>
              )}
            </div>

            {(officeNumberName || officeNumber) && (
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing(0.5),
                  alignItems: 'center',
                  lineHeight: theme.spacing(2.5),
                }}
              >
                {officeNumberName && (
                  <div
                    style={{
                      maxWidth: theme.spacing(20),
                    }}
                  >
                    <Truncated
                      style={{
                        margin: theme.spacing(0),
                      }}
                    >
                      <Text as='span' color='white' weight='medium' size='medium'>
                        {officeNumberName}
                      </Text>
                    </Truncated>
                  </div>
                )}
                {officeNumber && (
                  <Text as='span' color='white' size='medium'>
                    {formatPhoneNumber(officeNumber, true)}
                  </Text>
                )}
              </div>
            )}

            {!!callerContext && (
              <Trans t={t} callerContext={callerContext}>
                <div
                  style={{
                    display: 'flex',
                    gap: theme.spacing(0.5),
                  }}
                >
                  <Icon name='call-source' size={16} color='white' />
                  <Text as='span' color='white' weight='medium' size='small'>
                    via {callerContext ?? ''}
                  </Text>
                </div>
              </Trans>
            )}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplate:
                isHeadOfHousehold && isFromSameHousehold
                  ? `
                  "profile name actions" auto
                  "profile chip actions" auto
                  "profile number number" 1fr
                  "profile petdetails petdetails" auto 
                  / auto 1fr auto`
                  : `
                  "profile name actions" auto
                  "profile number actions" 1fr
                  "profile petdetails petdetails" auto 
                  / auto 1fr auto`,
              alignItems: 'start',
              columnGap: theme.spacing(1),
            }}
          >
            <div style={{ gridArea: 'profile', width: '48px' }}>
              {personId ? (
                <ProfileOverlays
                  photoData={photoData}
                  name={callerName ?? ''}
                  personId={personId}
                  isFromSameHousehold={isFromSameHousehold}
                  isMultipleContacts={isMultipleContacts}
                />
              ) : (
                <Avatar />
              )}
            </div>
            <div style={{ gridArea: 'name' }}>
              {personId && callerName ? (
                <TextLink
                  trackingId='call-pop-profile-link'
                  onClick={() => onInspect()}
                  css={{
                    textDecoration: 'underline',
                    color: theme.colors.primary40,
                    fontWeight: theme.font.weight.semibold,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {callerName}
                </TextLink>
              ) : (
                <Text color='white' weight='bold' as='span' textAlign='left'>
                  {callerName?.length ? callerName : t('Unknown Caller')}
                </Text>
              )}
            </div>
            {isHeadOfHousehold && isFromSameHousehold && (
              <div style={{ gridArea: 'chip' }}>
                <Chip.Household variant='warningNeutral' maxWidth='160px'>
                  {t('Head of Household')}
                </Chip.Household>
              </div>
            )}
            <div style={{ gridArea: 'number' }}>
              <Text as='span' size='medium' color='white'>
                {formattedNumber}
              </Text>
            </div>
            <div style={{ gridArea: 'petdetails', display: 'flex', gap: theme.spacing(0.5) }}>
              {firstPet ? (
                <>
                  <PetIcon imageType={firstPet.validatedImageType} color='white' size={16} />
                  <Text as='span' size='medium' color='white'>
                    <>{firstPet.name}</>
                    {pets.length > 1 ? <>{t(` +{{petCount}} more`, { petCount: pets.length - 1 })}</> : null}
                  </Text>
                </>
              ) : null}
            </div>
            <div style={{ gridArea: 'actions', display: 'flex', gap: theme.spacing(2) }}>{actions}</div>
          </div>
        </div>
      </section>
    </BaseNotificationOuter>
  );
};

const DefaultAction = ({ onDismiss }: { onDismiss: () => void }) => {
  const { t } = useTranslation('pop');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ActionButton
        trackingId='phn-pop-ignore'
        data-test-id='pop-ignore'
        onClick={onDismiss}
        css={{ background: theme.colors.neutral70, border: 'none', color: theme.colors.white }}
      >
        <XIcon />
      </ActionButton>
      <Text as='span' weight='light' size='small' color='white'>
        {t('Ignore')}
      </Text>
    </div>
  );
};

export const DefaultCallPop = (props: DefaultProps) => {
  return (
    <CallPopErrorBoundary
      key={props.id}
      id={props.id}
      autoDismiss={true}
      position={props.pos}
      total={props.count}
      onDismiss={props.options.onDismiss}
      onBack={props.onBack}
      onForward={props.onForward}
    >
      <CallPop {...props} actions={<DefaultAction onDismiss={props.options.onDismiss} />} />
    </CallPopErrorBoundary>
  );
};

const SoftphoneActions = ({ onReject, onAccept }: { onReject: () => void; onAccept: () => void }) => {
  const { t } = useTranslation('pop');

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ActionButton
          trackingId='softphone-callpop-reject'
          data-test-id='pop-ignore'
          onClick={() => onReject()}
          css={{ background: theme.colors.critical50, border: 'none', color: theme.colors.white }}
        >
          <div style={{ transform: 'rotate(135deg)' }}>
            <PhoneIcon />
          </div>
        </ActionButton>
        <Text as='span' weight='light' size='small' color='white'>
          {t('Ignore')}
        </Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ActionButton
          trackingId='softphone-callpop-answer'
          data-test-id='pop-answer'
          onClick={() => onAccept()}
          css={{ background: theme.colors.success40, border: 'none', color: theme.colors.white }}
        >
          <PhoneIcon />
        </ActionButton>
        <Text as='span' weight='light' size='small' color='white'>
          {t('Answer')}
        </Text>
      </div>
    </>
  );
};

export const SoftphoneCallPop = (props: SoftphoneProps) => {
  const onAccept = () => {
    props.options.onAccept(props.callerNumber);
  };

  const onReject = () => {
    props.options.onReject(props.callerNumber);
  };

  return (
    <CallPopErrorBoundary
      key={props.id}
      id={props.id}
      autoDismiss={false}
      position={props.pos}
      total={props.count}
      onBack={props.onBack}
      onForward={props.onForward}
      onDismiss={() => props.options.onReject(props.callerNumber)}
    >
      <CallPop {...props} actions={<SoftphoneActions onAccept={onAccept} onReject={onReject} />} />
    </CallPopErrorBoundary>
  );
};

export const CallPopWidget = ({ notifications }: { notifications: CallPopTypes.Notification[] }) => {
  const { activeIndex, setOutlet } = useCallPopStateSync();
  const { t } = useTranslation('pop');
  const { inspect, navigateToIndex } = CallPopInterface;

  const notification = notifications[activeIndex];

  if (!notification || notification.payload.contacts.length === 0) {
    if (notifications.length > 0) {
      sentry.error({
        error: 'Error displaying call pop',
        topic: 'phone',
        team: 'fe-ux-platform',
        addContext: {
          name: 'CallPopWidget',
          context: {
            traceId: notification?.trace_id,
            activeIndex,
            notificationCount: notifications?.length,
            contacts: notification?.payload?.contacts,
          },
        },
      });
    }

    return null;
  }

  const onBack = () => {
    navigateToIndex(activeIndex - 1);
  };
  const onForward = () => {
    navigateToIndex(activeIndex + 1);
  };

  const isMultipleContacts = notification.payload.contacts.length > 1;
  const matchedContacts = notification.payload.contacts.length;
  const isFromSameHouseHold = isMultipleContactsFromSameHousehold(notification.payload.contacts);
  const contactToShow = isFromSameHouseHold
    ? getHeadofHousehold(notification.payload.contacts)
    : notification.payload.contacts[0];
  const nameToShow = isFromSameHouseHold
    ? contactToShow?.callerName
    : t('{{contactToShow}} + {{count}} Contact Matches', {
        contactToShow: contactToShow?.callerName,
        count: matchedContacts - 1,
      });
  const pets = contactToShow?.pets ?? [];

  return (
    <>
      <CallPopWrapper
        id={notification.id}
        count={notifications.length}
        pos={activeIndex + 1}
        personId={contactToShow?.personId}
        photoData={notification.payload.photoData}
        isHeadOfHousehold={!!contactToShow?.householdId && contactToShow?.householdId === contactToShow?.patientId}
        isFromSameHousehold={isFromSameHouseHold}
        isMultipleContacts={isMultipleContacts}
        callerContext={notification.payload.callerContext ?? ''}
        callerName={nameToShow}
        callerNumber={contactToShow?.callerNumber ?? ''}
        locationName={notification.payload.recipientLocationName}
        onBack={onBack}
        onForward={onForward}
        onInspect={() => {
          if (notification.payload.contacts.length > 0) {
            setOutlet('profile');
            inspect(notification);
          }
        }}
        onAccept={() => {
          if (notification.payload.autoShowCallerProfile && notification.payload.contacts.length > 0) {
            inspect(notification);
          }
        }}
        notification={notification}
        pets={pets}
        officeNumberName={notification.payload?.officeNumberName ?? ''}
        officeNumber={notification.payload?.officeNumber ?? ''}
      />
    </>
  );
};

const SpecialIconButton = (props: IconButtonProps) => {
  return (
    <IconButton
      css={{
        color: theme.colors.neutral20,
        ':disabled': {
          color: theme.colors.neutral50,
          svg: {
            fill: theme.colors.neutral50,
          },
        },
        ':hover:not(:disabled), :focus': {
          background: 'initial',
          color: theme.colors.white,
          svg: {
            fill: theme.colors.white,
          },
        },
      }}
      {...props}
    />
  );
};

export const CallPopNotification = () => {
  const { outlet, notifications } = useCallPopStateSync();
  const { status } = useIpcShellProvider();
  const shouldShow =
    outlet === 'queue' && notifications.length && (!shell.isShell || (shell.isShell && status !== 'connected'));
  const x = '100%';

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          style={{
            top: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            padding: theme.spacing(1),
          }}
          initial={{ x }}
          animate={{ x: 0 }}
          exit={{ x }}
          transition={{ ease: 'easeOut', duration: 0.3 }}
        >
          <CallPopWidget notifications={notifications} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

type Color = (typeof contactColors)[number];
type ProfileColor = { fill: string; text: string };

const multipleProfileColors = [
  contactColors.find((color) => color.name === 'Yellow'),
  contactColors.find((color) => color.name === 'Seaweed'),
  contactColors.find((color) => color.name === 'Blue'),
] as Color[];

const profileColorsArray: ProfileColor[] = multipleProfileColors
  .filter((color) => color !== undefined)
  .map((color) => ({ fill: color.fill, text: color.text }));

const ProfileOverlays = ({
  name,
  personId,
  photoData,
  isMultipleContacts = false,
  isFromSameHousehold,
}: {
  name: string;
  personId: string;
  photoData?: string;
  isMultipleContacts: boolean;
  isFromSameHousehold: boolean;
}) => {
  const generateArray = (size: number) => Array.from({ length: size }, (_, index) => index);
  const size = isMultipleContacts ? 3 : 1;
  const indexes = generateArray(size);

  const stackedPhotos = indexes.map((index) => (
    <Photos.ProfilePhoto
      src={photoData}
      key={`${personId}-${index}`}
      name={isMultipleContacts && !isFromSameHousehold ? '' : name}
      personId={personId}
      bypassColor={profileColorsArray[index]}
      style={{
        border: `1px solid ${theme.colors.neutral90}`,
        borderRadius: '50%',
        position: 'absolute',
        right: `${index * 4}px`,
        zIndex: `${index}`,
      }}
    />
  ));

  return <div style={{ position: 'relative', width: '48px', height: '48px' }}>{stackedPhotos}</div>;
};

/**
 * Do not get fancy with this.
 * Do not use imported modules.
 * This needs to be impossible to accidentally break
 * */

export const CallPopErrorBoundary = ({
  id,
  position,
  total,
  children,
  onDismiss,
  autoDismiss,
}: {
  id: string;
  position: number;
  total: number;
  children: ReactNode;
  autoDismiss: boolean;
  onDismiss: () => void;
  onBack: () => void;
  onForward: () => void;
}) => {
  const Fallback = (errorProps: ComponentProps<FallbackRender>) => {
    const errorMessage = (errorProps?.error as Error)?.message || 'Unknown error';

    const safeResetError = () => {
      if (typeof errorProps?.resetError === 'function') {
        try {
          errorProps.resetError();
        } catch (e) {
          console.error('Failed to reset error:', e);
        }
      }
    };

    useEffect(() => {
      if (autoDismiss) {
        console.log('dismissing', position);
        onDismiss();
        // safeResetError();
      }
    }, [id]);

    const handleDismiss = () => {
      safeResetError();
      if (typeof onDismiss === 'function') {
        onDismiss();
      }
    };

    if (autoDismiss) {
      return <>{/* nothing to show, should be auto-dismissed */}</>;
    }

    return (
      <BaseNotificationOuter>
        <section css={{ padding: theme.spacing(2), background: theme.colors.neutral90, color: theme.colors.white }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <div
              style={{
                borderBottom: `1px solid ${theme.colors.neutral50}`,
                paddingBlockEnd: theme.spacing(1),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <strong style={{ color: theme.colors.critical50 }}>Error displaying call information</strong>
                {total > 1 && <div style={{ display: 'flex', alignItems: 'center' }}>{`${position}/${total}`}</div>}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                columnGap: theme.spacing(3),
              }}
            >
              <span
                style={{
                  color: theme.colors.neutral20,
                }}
              >
                {errorMessage}
              </span>
              <button
                onClick={handleDismiss}
                style={{
                  background: theme.colors.neutral70,
                  border: 'none',
                  color: theme.colors.white,
                  padding: theme.spacing(1),
                  borderRadius: theme.borderRadius.small,
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </section>
      </BaseNotificationOuter>
    );
  };
  return <ErrorBoundary fallback={Fallback}>{children}</ErrorBoundary>;
};
